import type { AxiosError, AxiosInstance } from 'axios'
import { isPublicPageRoute } from '@/utils/isPublicPageRoute.util'

let logoutInProgress = false
let interceptorInstalled = false

/** Reset one-shot / install flags (tests only). */
export function resetUnauthorizedLogoutInterceptorForTests() {
    logoutInProgress = false
    interceptorInstalled = false
}

function getLogoutUrl(): string {
    return '/auth/logout?returnTo=' + new URL('/api/auth/logout-callback', window.location.origin).toString()
}

function shouldSkipLogoutRedirect(pathname: string): boolean {
    if (pathname.startsWith('/auth/')) return true
    return isPublicPageRoute(pathname)
}

function clearClientStorage() {
    try {
        sessionStorage.clear()
        localStorage.clear()
    } catch {
        // ignore storage failures during forced logout
    }
}

export function handleUnauthorizedResponseError(error: AxiosError): Promise<never> {
    if (typeof window === 'undefined') {
        return Promise.reject(error)
    }

    if (error.response?.status !== 401) {
        return Promise.reject(error)
    }

    const pathname = window.location.pathname
    if (shouldSkipLogoutRedirect(pathname)) {
        return Promise.reject(error)
    }

    if (!logoutInProgress) {
        logoutInProgress = true
        clearClientStorage()
        window.location.replace(getLogoutUrl())
    }

    return Promise.reject(error)
}

/** Install a one-time 401 response interceptor on the OpenAPI Axios instance. */
export function installUnauthorizedLogoutInterceptor(instance: AxiosInstance) {
    if (interceptorInstalled) return
    interceptorInstalled = true

    instance.interceptors.response.use(
        (response) => response,
        (error: AxiosError) => handleUnauthorizedResponseError(error)
    )
}
